// src/features/account/components/PaymentMethodTab.jsx
// Simple payment method management - view/add/update card on file
import React, { useState, useEffect } from 'react';
import { CreditCard, Plus, Trash2, RefreshCw } from 'lucide-react';
import { useWorkOS } from '../../workos/context/WorkOSContext';
import AddPaymentMethodModal from './AddPaymentMethodModal';

const API_BASE = '/.netlify/functions';

const PaymentMethodTab = () => {
  const { getValidAccessToken } = useWorkOS();
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const fetchPaymentMethod = async () => {
    setLoading(true);
    setError(null);
    try {
      const accessToken = await getValidAccessToken();
      const response = await fetch(`${API_BASE}/check-payment-method`, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch payment method');
      }

      const data = await response.json();
      setPaymentMethod(data.paymentMethod);
    } catch (err) {
      console.error('Error fetching payment method:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethod();
  }, []);

  const handleRemoveCard = async () => {
    if (!confirm('Are you sure you want to remove this payment method?')) {
      return;
    }

    setIsRemoving(true);
    try {
      const accessToken = await getValidAccessToken();
      const response = await fetch(`${API_BASE}/remove-payment-method`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to remove payment method');
      }

      setPaymentMethod(null);
    } catch (err) {
      console.error('Error removing payment method:', err);
      alert(err.message);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleAddSuccess = () => {
    setShowAddModal(false);
    fetchPaymentMethod();
  };

  const getCardBrandIcon = (brand) => {
    // Could add brand-specific icons here
    return <CreditCard className="h-8 w-8 text-gray-400" />;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-2">Payment Method</h2>
        <p className="text-gray-400 text-sm">
          Manage your payment method for purchasing credits and paid workflows.
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-900/30 border border-red-600 rounded-lg text-red-400">
          {error}
        </div>
      )}

      {paymentMethod ? (
        // Card on file
        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {getCardBrandIcon(paymentMethod.brand)}
              <div>
                <p className="font-medium text-white capitalize">
                  {paymentMethod.brand} •••• {paymentMethod.last4}
                </p>
                <p className="text-sm text-gray-400">
                  Expires {paymentMethod.expMonth}/{paymentMethod.expYear}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Update
              </button>
              <button
                onClick={handleRemoveCard}
                disabled={isRemoving}
                className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors disabled:opacity-50"
              >
                {isRemoving ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        // No card on file
        <div className="bg-gray-800 rounded-lg p-8 text-center">
          <CreditCard className="h-12 w-12 text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No payment method</h3>
          <p className="text-gray-400 text-sm mb-6">
            Add a payment method to purchase credits and paid workflows.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="h-5 w-5" />
            Add Payment Method
          </button>
        </div>
      )}

      {/* Info box */}
      <div className="bg-gray-800/50 rounded-lg p-4 text-sm text-gray-400">
        <p>
          Your card will only be charged when you add credits or purchase paid workflows.
          We use Stripe for secure payment processing.
        </p>
        {paymentMethod && (
          <button
            onClick={async () => {
              try {
                const accessToken = await getValidAccessToken();
                const response = await fetch(`${API_BASE}/stripe-portal-session`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                  }
                });
                const data = await response.json();
                if (data.url) {
                  window.open(data.url, '_blank');
                } else if (data.error) {
                  alert(data.error);
                } else {
                  alert('Unable to open Stripe portal.');
                }
              } catch (err) {
                console.error('Failed to open Stripe portal:', err);
                alert('Failed to open Stripe portal.');
              }
            }}
            className="mt-2 text-blue-400 hover:text-blue-300 text-sm hover:underline"
          >
            Manage payment methods on Stripe →
          </button>
        )}
      </div>

      {/* Add/Update Payment Method Modal */}
      <AddPaymentMethodModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAddSuccess}
        context="default"
      />
    </div>
  );
};

export default PaymentMethodTab;
